// Auto-append `# user date [add reason here]` when a YAML setting value changes.
// Most of the complicated logic here is about:
// - the regex patterns to match on the various cases, to not repeat the pattern N times
// - detecting when the user has deleted the comment because they don't want it (which is ok),
//   so we don't immediately re-add it
// That logic is rather complex, completely AI-generated, seems to work well enough,
// and is isolated. So I don't think it's worth trying hard to make much simpler.
import { isYamlSettingBody, splitYamlComment } from "./helpers";

export interface TextSelection {
  start: number;
  end: number;
}

const GENERATED_ATTRIBUTION = String.raw`# \S+ \d{4}-\d{2}-\d{2} \[add reason here\]`;
const REASON_STYLE_COMMENT = String.raw`# \S+ \d{4}-\d{2}-\d{2}\b`;
const GENERATED_ATTRIBUTION_PATTERN = new RegExp(GENERATED_ATTRIBUTION);
const REASON_STYLE_COMMENT_PATTERN = new RegExp(REASON_STYLE_COMMENT);

export function shouldSkipAttributionForEdit(
  previousValue: string,
  nextValue: string,
) {
  if (nextValue.length >= previousValue.length) {
    return false;
  }

  const deletedRange = getDeletedRange(previousValue, nextValue);
  if (!deletedRange) {
    return false;
  }

  // include leading whitespace so deleting the space before `#` counts too.
  const pattern = new RegExp(String.raw`[^\S\n]*${GENERATED_ATTRIBUTION}`, "g");
  for (const match of previousValue.matchAll(pattern)) {
    const attributionStart = match.index;
    const attributionEnd = match.index + match[0].length;
    if (
      deletedRange.start < attributionEnd &&
      deletedRange.end > attributionStart
    ) {
      return true;
    }
  }

  return false;
}

function getDeletedRange(previousValue: string, nextValue: string) {
  let start = 0;
  while (
    start < previousValue.length &&
    start < nextValue.length &&
    previousValue[start] === nextValue[start]
  ) {
    start++;
  }

  let previousEnd = previousValue.length;
  let nextEnd = nextValue.length;
  while (
    previousEnd > start &&
    nextEnd > start &&
    previousValue[previousEnd - 1] === nextValue[nextEnd - 1]
  ) {
    previousEnd--;
    nextEnd--;
  }

  if (previousEnd === start) {
    return null;
  }

  return { start, end: previousEnd };
}

export function appendChangeComments(
  previousValue: string,
  nextValue: string,
  selection: TextSelection,
  username: string,
  now = new Date(),
) {
  const date = formatUtcDate(now);
  const attributionUsername = formatAttributionUsername(username);
  const previousLines = previousValue.split("\n");
  const nextLines = nextValue.split("\n");
  const changedNextIndexes = alignChangedLines(previousLines, nextLines);
  let rawOffset = 0;
  let selectionStartDelta = 0;
  let selectionEndDelta = 0;

  const value = nextLines
    .map((line, index) => {
      if (!changedNextIndexes.has(index)) {
        rawOffset += line.length + 1;
        return line;
      }

      // Same-length edits can use the same index for the previous body. After an
      // insert/delete, that index is a different line, so do not compare against it.
      const nextLine = appendChangeCommentToLine(
        previousLines.length === nextLines.length
          ? (previousLines[index] ?? "")
          : "",
        line,
        date,
        attributionUsername,
      );
      const delta = nextLine.length - line.length;
      const insertionOffset = rawOffset + line.length;

      if (delta !== 0) {
        if (insertionOffset < selection.start) {
          selectionStartDelta += delta;
        }
        if (insertionOffset < selection.end) {
          selectionEndDelta += delta;
        }
      }

      rawOffset += line.length + 1;
      return nextLine;
    })
    .join("\n");

  return {
    value,
    selection: {
      start: selection.start + selectionStartDelta,
      end: selection.end + selectionEndDelta,
    },
  };
}

// Longest common subsequence of exact lines: inserts/deletes do not mark shifted
// lines as edited. On ties, prefer marking the earlier next line as changed so
// editing a line to match a later duplicate attributes the line that was edited.
function alignChangedLines(previousLines: string[], nextLines: string[]) {
  if (previousLines.length * nextLines.length > 40000) {
    return new Set(
      nextLines
        .map((line, index) => (line === previousLines[index] ? null : index))
        .filter((index): index is number => index != null),
    );
  }

  const dp = Array.from({ length: previousLines.length + 1 }, () =>
    Array(nextLines.length + 1).fill(0),
  );

  for (let i = previousLines.length - 1; i >= 0; i--) {
    for (let j = nextLines.length - 1; j >= 0; j--) {
      dp[i][j] =
        previousLines[i] === nextLines[j]
          ? dp[i + 1][j + 1] + 1
          : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const matchedNextIndexes = new Set<number>();
  let previousIndex = 0;
  let nextIndex = 0;

  while (previousIndex < previousLines.length && nextIndex < nextLines.length) {
    if (previousLines[previousIndex] === nextLines[nextIndex]) {
      matchedNextIndexes.add(nextIndex);
      previousIndex++;
      nextIndex++;
    } else if (
      dp[previousIndex + 1][nextIndex] > dp[previousIndex][nextIndex + 1]
    ) {
      previousIndex++;
    } else {
      nextIndex++;
    }
  }

  return new Set(
    nextLines
      .map((_, index) => (matchedNextIndexes.has(index) ? null : index))
      .filter((index): index is number => index != null),
  );
}

function appendChangeCommentToLine(
  previousLine: string,
  nextLine: string,
  date: string,
  username: string,
) {
  const previousBody = splitYamlComment(previousLine).body.trim();
  const { body, comment } = splitYamlComment(nextLine);
  const trimmedBody = body.trim();

  if (!isYamlSettingBody(trimmedBody) || previousBody === trimmedBody) {
    return nextLine;
  }

  const attribution = `# ${username} ${date} [add reason here]`;

  if (comment && GENERATED_ATTRIBUTION_PATTERN.test(comment)) {
    return nextLine.replace(GENERATED_ATTRIBUTION_PATTERN, attribution);
  }

  // don't append another reason if there's already one
  if (REASON_STYLE_COMMENT_PATTERN.test(nextLine)) {
    return nextLine;
  }

  return `${nextLine} ${attribution}`;
}

function formatAttributionUsername(username: string) {
  const trimmedUsername = username.trim();
  const attributionUsername = trimmedUsername.includes("@")
    ? trimmedUsername.split("@", 1)[0]
    : trimmedUsername;

  return attributionUsername.replace(/\s+/g, "_") || "user";
}

function formatUtcDate(date: Date) {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}
